<template>
  <wj-flex-grid
    :id="componentId"
    :items-source="itemsSource"
    :initialized="initGrid"
    key-action-tab="Cycle"
    :allow-sorting="false"
    :allow-add-new="false"
    :allow-resizing="0"
    allow-dragging="None"
    :auto-generate-columns="false"
    :item-formatter="itemFormatter"
    :pasting-cell="onPastingCell"
    :pasted-cell="onPastedCell"
    headers-visibility="All"
    :scroll-position-changed="scrollChanged"
    :cell-edit-ending="cellEditEnding"
    :cell-edit-ended="fixActiveCell"
    :updated-view="fixActiveCell"
    :is-read-only="!editMode"
    :selection-changed="onSelectionChanged"
    :beginning-edit="beginningEdit"
    :allow-delete="false"
    :pasting="pasting"
    :copying="copying"
    :copying-cell="onCopyingCell"
    :show-marquee="true"
    :show-alternate-rows="true"
    :updated-layout="updatedLayout"
    :data-language-direction="languageDirection"
    :class="[themeClass, 'row-hover', 'row-lines', 'col-lines']"
    :show-selected-headers="3"
  >
    <wj-flex-grid-column v-for="column in columns"
      :key="column.key"
      :binding="column.key"
      :header="column.value"
      :min-width="column.minWidth"
      :width="column.width"
      :visible="column.visible"
      >
    </wj-flex-grid-column>
  </wj-flex-grid>
</template>
<script>
// The eslint no-undef exception is needed below to 'aprove' usage of wijmo.Control...
// https://eslint.org/docs/rules/no-undef
/* global wijmo:true */
/* eslint no-undef: "error" */
import _ from 'lodash'
import limits from '@/clipboard/limits'
import utils from '@/utils'
import { mapActions } from 'vuex'
import { isRtlLanguage } from '@/i18n'

export default {
  props: {
    columns: {
      type: Array,
      required: true
    },
    componentId: {
      type: String,
      required: true
    },
    userId: {
      type: Number,
      required: true
    },
    managerId: {
      type: String,
      required: true
    },
    itemsSource: {
      type: Array,
      required: true
    },
    itemsMetadata: {
      type: Array,
      required: true
    },
    scrollPositionChanged: {
      type: Function,
      required: true
    },
    editable: {
      type: Function,
      required: true
    },
    cellStyles: {
      type: Function,
      required: true
    },
    language: {
      type: String,
      required: true
    },
    theme: {
      type: String,
      required: true
    },
    columnHeaderCellStyles: {
      type: Function,
      required: true
    }
  },
  data () {
    return {
      editMode: true,
      cancelAction: false
    }
  },
  watch: {
    itemsSource: function (val) {
      this.refreshGrid()
    },
    itemsMetadata: function (val) {
      this.refreshGrid()
    }
  },
  computed: {
    languageDirection: function () {
      return isRtlLanguage(this.language) ? 'rtl' : 'ltr'
    },
    themeClass: function () {
      return this.theme
    }
  },
  methods: {
    ...mapActions('report', [
      'addError',
      'editCell'
    ]),
    initGrid (s, e) {
      // Preventing a copy, cut and paste of more than the limit requires 2 steps
      // We need to add event listeners to the document for cut, copy paste on document and call prevent default if needed
      // then we also need to listen for the flexgrid events like pasting, and cancel those events if needed
      // If we don't do this second step, then the first cell gets edited even though it was cancelled
      // Note: there is no e2e test for pasting of more than the row limit
      const evaluateAction = (ev) => {
        if (limits.actionWithinRowLimit(s.selection.rowSpan)) {
          const data = s.getClipString()
          ev.clipboardData.setData('text/plain', data)
        } else {
          this.cancelAction = true
        }
        ev.preventDefault()
      }
      // Detect if within the Textbox to allow normal OS Clipboard Operations (i.e.. copy partial strings)
      // Would have preferred to use Element.matches() but needs a Polyfill for IE11
      const inEditor = (ev) => (ev.srcElement.tagName === 'INPUT' && ev.srcElement.classList.contains('wj-grid-editor'))
      document.addEventListener('cut', (ev) => {
        if (!inEditor(ev)) {
          evaluateAction(ev)
          this.$emit('cut')
        }
      }, true)
      document.addEventListener('copy', (ev) => {
        if (!inEditor(ev)) {
          evaluateAction(ev)
          this.$emit('copy')
        }
      }, true)
      this.gridComponent().autoSizeRow(0, true)
      document.addEventListener('paste', ev => {
        if (!inEditor(ev)) {
          const parsed = ev.clipboardData.getData('text/plain').trimRight().split('\n')
          if (!limits.actionWithinRowLimit(parsed.length)) {
            this.cancelAction = true
            ev.preventDefault()
            console.log('error')
            const err = new Error(utils.bulkEditErrorMessage('paste'))
            this.addError(err)
          } else {
            this.cancelAction = false
          }
        }
      }, true)
    },
    refreshGrid () {
      const selection = this.gridComponent().selection
      this.gridComponent().collectionView.refresh()
      this.gridComponent().select(selection, false)
    },
    onPastingCell (s, e) {
      this.executeAction(e, () => { this.checkEditable(s, e) })
    },
    gridComponent () {
      return wijmo.Control.getControl(document.getElementById(this.componentId))
    },
    updatedLayout () {
      // the auto resize needs to be in this method
      // however calling auto resize results in updatedLayout being called again
      // this check prevents an infinate loop
      if (!this.autoSizing) {
        this.autoSizing = true
        this.gridComponent().autoSizeRow(0, true)
      } else {
        this.autoSizing = false
      }
    },
    scrollChanged: _.throttle(function () {
      this.scrollPositionChanged()
    }, 500, {
      'leading': true,
      'trailing': true
    }),
    itemFormatter (panel, r, c, cell) {
      if (panel === this.gridComponent().columnHeaders) {
        cell.innerHTML = '<div class="wj-header-content">' + cell.textContent + '</div>'
        cell.classList.add(this.columnHeaderCellStyles(c))
      }
      if (panel.cellType === wijmo.grid.CellType.Cell) {
        cell.classList.add(`cell-row${r + 1}-col${c + 1}`) // adding a class to help e2e testing
        this.cellStyles(r, c).forEach((style) => cell.classList.add(style))
      }
      if (panel.cellType === wijmo.grid.CellType.RowHeader && panel.grid.rowHeaders.enabled !== false) {
        cell.textContent = (r + 1).toString()
      }
    },
    cellEditEnding (sender, e) {
      // when we are 'deleting' a cells value, the active editor is null
      const cancel = e.cancel
      if (!cancel) {
        const data = sender.activeEditor ? sender.activeEditor.value : ''
        this.cellEdited(data, e)
      }
    },
    beginningEdit (sender, e) {
      if (!limits.actionWithinRowLimit(sender.selection.rowSpan)) {
        e.cancel = true
        // we only get here if copy/cut/paste has already been handled so this event must be clear
        this.addError(new Error(utils.bulkEditErrorMessage('clear')))
      }
      this.checkEditable(sender, e)
    },
    onPastedCell (sender, e) {
      const data = e.data
      this.cellEdited(data, e)
    },
    cellEdited (data, e) {
      // extract new value and co-ordinates
      const row = e.row
      const col = e.col
      this.editCell({row, col, data})
        .then((value) => { this.gridComponent().setCellData(row, col, value) })
    },
    pasting (sender, e) {
      // Override wijimo's default method
    },
    copying (sender, e) {
      // Override wijimo's default method
    },
    onCopyingCell (sender, e) {
      // Override wijimo's default method
    },
    onSelectionChanged (sender, e) {
      const result = this.gridComponent().getClipString()
      let selection = null
      if (e.range.isSingleCell) {
        selection = {
          isSingleCell: e.range.isSingleCell,
          topRow: e.range.row,
          bottomRow: e.range.col,
          selectedRow: e.row,
          selectedCol: e.col,
          rowSpan: e.range.rowSpan
        }
      } else {
        selection = {
          isSingleCell: e.range.isSingleCell,
          topRow: e.range.topRow,
          bottomRow: e.range.bottomRow,
          col: e.range.col,
          col2: e.range.col2,
          selectedRow: e.row,
          selectedCol: e.col,
          rowSpan: e.range.rowSpan
        }
      }
      this.$emit('selectionchange', result, selection)
    },
    checkEditable (s, e) {
      // currently only for single cells
      const row = e.row
      const col = e.col
      const isEditable = this.editable(row, col)
      if (!isEditable) {
        // cancels the edit event if cell is not editable
        e.cancel = true
      }
    },
    fixActiveCell (grid, args) {
      // This is to fix the defect in flexgrid for wj-state-selected which gets removed if show-marquee is true
      // https://www.grapecity.com/en/forums/wijmo/wijmo-5-flexgrid-wj-state-#interestingly-the-class-ap
      // Get Positions
      const destination = { row: grid.selection.row, column: grid.selection.col }
      // Get Last Cell Element
      const last = grid.cells.getCellElement(destination.row || 0, destination.column || 0)
      // Attach Classes
      if (last !== null) {
        last.classList.add('wj-state-selected')
        last.classList.add('wj-state-multi-selected')
      }
    },
    executeAction (e, func = () => {}) {
      if (this.cancelAction) {
        e.cancel = true
      } else {
        func()
      }
    }
  },
  mounted () {
    this.gridComponent().autoSizeRow(0, true)
    this.gridComponent().autoSizeColumn(0, true, 6)
  }
}
</script>
<style scope>
</style>
